import { useQuery } from "@tanstack/react-query";
import { ReactComponent as OutlineChevronDown } from "assets/svg/outline-chevron-down.svg";
import { ReactComponent as OutlineDocumentFilled } from "assets/svg/outline-document-filled.svg";
import { ReactComponent as OutlineHourglass } from "assets/svg/outline-hourglass.svg";
import { ReactComponent as OutlinePlus } from "assets/svg/outline-plus.svg";
import { ReactComponent as OutlineSearchGray } from "assets/svg/outline-search-gray.svg";
import {
  AI_APPOINTMENT_TYPES,
  AUDIO_SESSION_TYPES,
  REGULAR_AI_SESSION_TYPE,
} from "components/ai-session-review/constants";
import RecordingUploadZone from "components/ai-session-review/recording-upload/RecordingUploadZone";
import { isAIAudioSession } from "components/ai-session-review/utils/getAISessionType";
import SessionDateAndTime from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-sidebar/components/add-to-calendar/components/add-or-edit-session/components/session-time-and-date/SessionTimeAndDate";
import SessionTypeItem from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-sidebar/components/add-to-calendar/components/add-or-edit-session/components/session-type-selector/SessionTypeItem";
import isTimeValid from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-sidebar/components/add-to-calendar/utils/isTimeValid";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import PatientCreatorContainer from "components/patient-editor/PatientCreatorContainer";
import RadioButton from "components/radio-button";
import { useClientsContext } from "contexts/ClientsContext";
import { useSessionTypes } from "contexts/SessionTypesContext";
import { useUser } from "contexts/UserContext";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import attachNoteToAppointment from "services/scheduler/attachNoteToAppointment";
import postAppointmentSeries from "services/scheduler/postAppointmentSeries";
import postSessionRecordingMetadata from "services/transcription/postSessionRecordingMetadata";
import { notesCustomApi } from "store/services/notesCustomApi";
import { schedulerApi } from "store/services/scheduler";
import { AmplitudePageName /*, track  */ } from "utils/amplitude";
import getClasses from "utils/getClasses";
import getClientDisplayName from "utils/getClientDisplayName";
import getSessionEndFromSessionLength from "utils/getSessionEndFromSessionLength";
import searchClientsByName from "utils/searchClientsByName";
import styles from "./ScheduleSessionModal.module.scss";
import { listSchemasQueryOptions } from "queries/schemaQueryOptions";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "ai-home";

interface Props {
  show: boolean;
  handleHide: () => void;
  validatedError?: any;
  busy?: any;
  title: string;
  generateRedirectURL?: (
    appointmentId: string,
    fileName?: string | null,
    fileSize?: string | null,
  ) => string;
  buttonText: string;
  allowRecordingUpload: boolean;
  appointmentTypeLabel?: string;
  patientUserId?: string;
}

const ScheduleSessionModal = ({
  show,
  handleHide,
  validatedError,
  busy,
  title,
  generateRedirectURL,
  buttonText,
  allowRecordingUpload,
  appointmentTypeLabel: initialAppointmentTypeLabel,
  patientUserId,
}: Props) => {
  const [appointmentTypeLabel, setAppointmentTypeLabel] = useState<string>(
    initialAppointmentTypeLabel || REGULAR_AI_SESSION_TYPE,
  );

  const dispatch = useDispatch();
  const { defaultUserTimezone } = useUser();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<PatientChartContact>();

  const { sessionTypes } = useSessionTypes();

  const [timeValue, setTimeValue] = useState<string | undefined>();
  const [dateValue, setDateValue] = useState<number | undefined>();

  useEffect(() => {
    const momentInitialIntervalStart = moment.tz(
      new Date(),
      defaultUserTimezone,
    );
    const timeValue = momentInitialIntervalStart.format("HH:mm");
    setTimeValue(timeValue);
    setDateValue(momentInitialIntervalStart.startOf("day").valueOf());
  }, [defaultUserTimezone]);

  const startISO = useMemo(() => {
    if (!isTimeValid(timeValue) || !dateValue) {
      return "";
    }

    return moment
      .tz(
        moment
          .tz(dateValue, defaultUserTimezone)
          .format(`YYYY-MM-DD ${timeValue}`),
        defaultUserTimezone,
      )
      .toISOString();
  }, [dateValue, timeValue, defaultUserTimezone]);

  const [, setBusy] = useState(false);
  const [error, setError] = useState<any>();
  const [s3Key, setS3Key] = useState<string>();
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<string | null>(null);

  const [audioSessionType, setAudioSessionType] = useState<AudioSessionType>(
    AUDIO_SESSION_TYPES.individual.id,
  );

  const [noteTemplateSearch, setNoteTemplateSearch] = useState("");
  const noteSchemasQuery = useQuery(listSchemasQueryOptions("note"));
  const noteSchemas = useMemo(
    () => noteSchemasQuery.data ?? [],
    [noteSchemasQuery.data],
  );
  const [noteSchema, setNoteSchema] = useState<NoteSchema>();

  const filtered = useMemo(() => {
    if (!noteTemplateSearch) {
      return noteSchemas;
    }

    return noteSchemas.filter((el) =>
      el.title?.toLowerCase().includes(noteTemplateSearch.toLowerCase()),
    );
  }, [noteSchemas, noteTemplateSearch]);

  const currSelectedNoteSchema = noteSchemas.find(
    (el) => el.note_schema_id === noteSchema?.note_schema_id,
  );

  const audioSessionSource = useMemo(() => {
    if (allowRecordingUpload) {
      return "upload";
    } else if (isAIAudioSession(appointmentTypeLabel)) {
      return "live-audio-recording";
    } else {
      return undefined;
    }
  }, [appointmentTypeLabel, allowRecordingUpload]);

  const attachNewNote = useCallback(
    async (
      patientId: string,
      appointmentId: number,
      noteSchema: NoteSchema,
    ) => {
      if (!patientId || !appointmentId) return;

      await attachNoteToAppointment({
        appointmentId,
        patientId,
        noteSchema,
      });

      // TODO: for some reason this does not work
      dispatch(
        notesCustomApi.util.invalidateTags([
          {
            type: "note-for-appointment",
            id: appointmentId,
          },
        ]),
      );
    },
    [dispatch],
  );

  const createVideoOrAudioSession = useCallback(
    async (patientId: string, appointmentType: string) => {
      setBusy(true);

      const selectedSessionType = sessionTypes.find(
        (el) => el.label === appointmentTypeLabel,
      );

      if (!selectedSessionType) {
        setError("No session type selected");
        return;
      }

      try {
        const singleSession = {
          appointment_end: getSessionEndFromSessionLength(
            startISO,
            selectedSessionType.length_min,
          ),
          appointment_start: startISO,
          place_session_held_outside_of_orchid: undefined,
          external_meeting_link: undefined,
        };

        const response = await postAppointmentSeries({
          appointment_type_label: appointmentType
            ? appointmentType
            : appointmentTypeLabel,
          force_book_over_blocked: true,
          patient_user_id: patientId,
          repeat_rule: null,
          appointments: [singleSession],
          invoicing: {
            automation: "manual",
          },
          skip_email: true,
        });

        const appointmentId = response.invites_created[0].appointment_id;

        // Setting the metadata is only necessary for audio file
        // sessions (upload or live recording)
        if (audioSessionSource) {
          await postSessionRecordingMetadata({
            appointmentId,

            // For audio file uploads, we should have the s3 key at this point and
            // should record it. For live audio recordings, we don't have the s3 key
            // until after the recording, but we should
            // have pre-identified the session type
            s3Key,

            // We set the session type so that AWS Transcribe has an idea of how many
            // voices it should try to differentiate from the audio file (1 for dictation,
            // 2 for individual-patient, 3 for couple)
            sessionType: audioSessionType,
            sessionSource: audioSessionSource,
          });
        }

        if (noteSchema) {
          // if there is note template attached
          attachNewNote(patientId, appointmentId, noteSchema);
        }

        dispatch(schedulerApi.util.invalidateTags(["appointment"]));

        generateRedirectURL &&
          navigate(
            `${generateRedirectURL(
              appointmentId.toString(),
              fileName,
              fileSize,
            )}`,
          );
        handleHide();
      } catch (error: any) {
        console.error(error);
        setError(error);
      } finally {
        setBusy(false);
      }
    },
    [
      dispatch,
      startISO,
      navigate,
      generateRedirectURL,
      s3Key,
      fileName,
      fileSize,
      appointmentTypeLabel,
      handleHide,
      audioSessionSource,
      audioSessionType,
      noteSchema,
      attachNewNote,
      sessionTypes,
    ],
  );

  const [showPatientCreator, setShowPatientCreator] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const {
    individuals,
    couples,
    // isLoadingClients,
    initializeClients,
    refreshClients,
  } = useClientsContext();

  const clientsSorted = useMemo(() => {
    const fullList = [...individuals, ...couples];

    return searchClientsByName(fullList, searchTerm)
      .sort((a, b) =>
        getClientDisplayName(a) < getClientDisplayName(b) ? -1 : 1,
      )
      ?.filter((client) => client.patient.client_status === "patient");
  }, [individuals, couples, searchTerm]);

  const currentClient = [...individuals, ...couples].find((client) =>
    !!patientUserId
      ? client.patient.orchid_user_id === patientUserId
      : client.patient.orchid_user_id === patient?.orchid_user_id,
  );
  const currentClientDisplayName = currentClient
    ? getClientDisplayName(currentClient)
    : "";

  useEffect(() => {
    initializeClients();
  }, [initializeClients]);

  const showShowGeneralSessionTypeToggle = useMemo(() => {
    return (
      !initialAppointmentTypeLabel ||
      (initialAppointmentTypeLabel &&
        !isAIAudioSession(initialAppointmentTypeLabel))
    );
  }, [initialAppointmentTypeLabel]);

  const showShowSessionTypeDivider = useMemo(() => {
    return (
      !initialAppointmentTypeLabel && isAIAudioSession(appointmentTypeLabel)
    );
  }, [initialAppointmentTypeLabel, appointmentTypeLabel]);

  const showShowAudioSessionTypeToggle = useMemo(() => {
    return (
      (initialAppointmentTypeLabel &&
        isAIAudioSession(initialAppointmentTypeLabel)) ||
      (!initialAppointmentTypeLabel && isAIAudioSession(appointmentTypeLabel))
    );
  }, [initialAppointmentTypeLabel, appointmentTypeLabel]);

  return !showPatientCreator ? (
    <ModalLayout
      centered={true}
      showHeaderDivider={true}
      show={show}
      onHide={() => {
        handleHide();
        setError(undefined);
      }}
      backdrop="static"
      keyboard={false}
      error={error || validatedError}
      Header={title}
      Footer={
        <div className={styles["custom-footer"]}>
          <Button
            variant={"link"}
            onClick={() => {
              setShowPatientCreator(true);
            }}
            className={styles["custom-footer__add-client-button"]}
          >
            <OutlinePlus />
            Add a New Client
          </Button>
          <div className={styles["custom-footer__button-group"]}>
            <Button
              disabled={busy}
              variant={"outline-primary"}
              onClick={() => {
                handleHide();
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={
                busy ||
                (!s3Key && allowRecordingUpload) ||
                (!patient?.orchid_user_id && !patientUserId)
              }
              onClick={() => {
                createVideoOrAudioSession(
                  !!patientUserId
                    ? patientUserId
                    : patient?.orchid_user_id || "",

                  appointmentTypeLabel,
                );
              }}
            >
              {buttonText}
            </Button>
          </div>
        </div>
      }
      headerClass={styles["header"]}
      bodyClass={styles["body"]}
      footerClass={styles["footer"]}
      dialogClassName={styles["dialog"]}
      contentClassName={styles["content"]}
    >
      <Form.Group className={styles["session-type"]}>
        <Form.Label className={styles["label"]}>Session type</Form.Label>
        {showShowGeneralSessionTypeToggle && (
          <div
            className={styles["session-type__radio-group"]}
            role="radiogroup"
            aria-label="video audio session type"
          >
            {Object.values(AI_APPOINTMENT_TYPES).map((el) => (
              <RadioButton
                key={el.id}
                name={"video-audio-session-type"}
                label={el.titleStringId}
                value={el.id}
                onChange={(e) => {
                  e.target.value !== appointmentTypeLabel &&
                    setAppointmentTypeLabel(e.target.value);
                }}
                checked={appointmentTypeLabel === el.id}
              />
            ))}
          </div>
        )}

        {showShowSessionTypeDivider && <hr />}

        {showShowAudioSessionTypeToggle && (
          <div
            className={styles["session-type__radio-group"]}
            role="radiogroup"
            aria-label="audio session type"
          >
            {Object.values(AUDIO_SESSION_TYPES).map((el) => (
              <RadioButton
                key={el.id}
                name={"audio-session-type"}
                label={el.titleStringId}
                value={el.id}
                onChange={(e) => {
                  e.target.value !== audioSessionType &&
                    setAudioSessionType(e.target.value as AudioSessionType);
                }}
                checked={audioSessionType === el.id}
              />
            ))}
          </div>
        )}
      </Form.Group>
      <div className={styles["invite-client"]}>
        <Form.Label className={styles["label"]}>Invite Client</Form.Label>
        <DropdownToggle
          disabled={!!patientUserId}
          titleClassName={getClasses(
            styles["title"],
            !!patientUserId && styles["title--disabled"],
          )}
          menuClassName={styles["menu"]}
          title={
            <div>
              <OutlineSearchGray />
              <input
                type="text"
                placeholder="Search client name"
                onChange={(event) => {
                  setPatient(undefined);
                  setSearchTerm(event.currentTarget.value);
                }}
                value={
                  currentClientDisplayName
                    ? currentClientDisplayName
                    : searchTerm
                }
                autoFocus
              />
            </div>
          }
          Caret={<OutlineChevronDown />}
        >
          {clientsSorted.map((client) => {
            return (
              <Dropdown.Item
                key={client.patient.orchid_user_id}
                onClick={() => {
                  setPatient(client.patient);
                }}
              >
                {getClientDisplayName(client)}
              </Dropdown.Item>
            );
          })}
        </DropdownToggle>
      </div>
      <div className={styles["date-time"]}>
        <SessionDateAndTime
          dateValue={dateValue}
          setIsDirty={() => {}}
          setDateValue={setDateValue}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          trackClick={() => {}}
          canChangeDate
        />
      </div>
      <div className={styles["duration"]}>
        <Form.Label className={styles["label"]}>Duration</Form.Label>
        <DropdownToggle
          Caret={<OutlineChevronDown />}
          title={
            !appointmentTypeLabel ||
            !sessionTypes.find((el) => el.label === appointmentTypeLabel) ? (
              <div className={styles["duration__title"]}>
                <OutlineHourglass />
                Select a session type
              </div>
            ) : (
              <SessionTypeItem
                sessionType={
                  sessionTypes.find((el) => el.label === appointmentTypeLabel)!
                }
              />
            )
          }
        >
          {sessionTypes.map((el) => {
            return (
              <Dropdown.Item
                key={el.label}
                onClick={() => {
                  setAppointmentTypeLabel(el.label);
                }}
              >
                <SessionTypeItem sessionType={el} />
              </Dropdown.Item>
            );
          })}
        </DropdownToggle>
      </div>
      <div className={styles["note-type"]}>
        <Form.Label className={styles["label"]}>
          Progress Note (optional)
        </Form.Label>
        <DropdownToggle
          menuClassName={styles["menu"]}
          titleClassName={styles["title"]}
          title={
            <div>
              <OutlineDocumentFilled />
              <input
                type="text"
                placeholder="Choose from below template"
                onChange={(event) => {
                  setNoteSchema(undefined);
                  setNoteTemplateSearch(event.currentTarget.value);
                }}
                value={
                  currSelectedNoteSchema
                    ? currSelectedNoteSchema.title
                    : noteTemplateSearch
                }
                autoFocus
              />
            </div>
          }
          Caret={<OutlineChevronDown />}
        >
          {filtered.map((el) => (
            <Dropdown.Item
              key={el.note_schema_id}
              onClick={() => {
                setNoteSchema(el);
                setError(undefined);
                setBusy(false);
              }}
            >
              {el.title}
            </Dropdown.Item>
          ))}
        </DropdownToggle>
      </div>
      {allowRecordingUpload && <hr />}
      {allowRecordingUpload ? (
        <RecordingUploadZone
          onUploadComplete={(
            s3Key: string,
            fileName: string,
            fileSize: string,
          ) => {
            setS3Key(s3Key);
            setFileName(fileName);
            setFileSize(fileSize);
          }}
          setError={setError}
          amplitudePageName={AMPLITUDE_PAGE_NAME}
          doConfirmReupload={false}
          preventDeletion={false}
          isInScheduleModal={true}
        />
      ) : (
        <div className={styles["spacer"]} />
      )}
    </ModalLayout>
  ) : (
    <PatientCreatorContainer
      show={true}
      onHide={() => {
        setShowPatientCreator(false);
      }}
      nextAction={() => {
        refreshClients();
      }}
      amplitudePageName={AMPLITUDE_PAGE_NAME}
    />
  );
};

export default ScheduleSessionModal;
